import { useEffect, useMemo, useRef, useState } from "react";
import VestaSelect from "../common/VestaSelect";
import { OverlayFieldLabel } from "../common/OverlayValidation";

const XLSX_CDN_URL = "https://cdn.jsdelivr.net/npm/xlsx@0.18.5/dist/xlsx.full.min.js";

function cleanText(value) {
  return String(value ?? "").trim();
}

function normalizeToken(value) {
  return cleanText(value)
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "");
}

function isBlank(value) {
  return value === undefined || value === null || String(value).trim() === "";
}

function setDeepValue(target, path, value) {
  const parts = String(path || "").split(".").filter(Boolean);
  if (!parts.length) return;
  let cursor = target;
  parts.forEach((part, index) => {
    if (index === parts.length - 1) {
      cursor[part] = value;
      return;
    }
    if (!cursor[part] || typeof cursor[part] !== "object" || Array.isArray(cursor[part])) cursor[part] = {};
    cursor = cursor[part];
  });
}

function readDeepValue(source, path) {
  return String(path || "").split(".").filter(Boolean).reduce((value, part) => value?.[part], source);
}

function parseBoolean(value) {
  if (typeof value === "boolean") return value;
  const text = normalizeToken(value);
  if (["si", "s", "true", "1", "yes", "y", "attivo", "attiva", "vero"].includes(text)) return true;
  if (["no", "n", "false", "0", "disattivo", "disattiva", "falso"].includes(text)) return false;
  return Boolean(value);
}

function formatBoolean(value) {
  return value === false ? "No" : "Sì";
}

function csvEscape(value, separator = ";") {
  const text = String(value ?? "");
  if (text.includes('"') || text.includes("\n") || text.includes("\r") || text.includes(separator)) return `"${text.replace(/"/g, '""')}"`;
  return text;
}

function detectCsvSeparator(text) {
  const firstLine = String(text || "").split(/\r?\n/).find((line) => line.trim()) || "";
  const candidates = [";", ",", "\t"];
  return candidates
    .map((separator) => ({ separator, count: firstLine.split(separator).length }))
    .sort((a, b) => b.count - a.count)[0]?.separator || ";";
}

function parseCsv(text) {
  const separator = detectCsvSeparator(text);
  const rows = [];
  let row = [];
  let cell = "";
  let quoted = false;
  const source = String(text || "").replace(/^\uFEFF/, "");
  for (let index = 0; index < source.length; index += 1) {
    const char = source[index];
    const next = source[index + 1];
    if (char === '"') {
      if (quoted && next === '"') {
        cell += '"';
        index += 1;
      } else {
        quoted = !quoted;
      }
      continue;
    }
    if (!quoted && char === separator) {
      row.push(cell);
      cell = "";
      continue;
    }
    if (!quoted && (char === "\n" || char === "\r")) {
      if (char === "\r" && next === "\n") index += 1;
      row.push(cell);
      if (row.some((value) => !isBlank(value))) rows.push(row);
      row = [];
      cell = "";
      continue;
    }
    cell += char;
  }
  row.push(cell);
  if (row.some((value) => !isBlank(value))) rows.push(row);
  return rows;
}

function rowsFromMatrix(matrix) {
  const cleanMatrix = (Array.isArray(matrix) ? matrix : []).filter((row) => Array.isArray(row) && row.some((value) => !isBlank(value)));
  if (!cleanMatrix.length) return { columns: [], rows: [] };
  const headers = cleanMatrix[0].map((value, index) => cleanText(value) || `Colonna ${index + 1}`);
  const uniqueHeaders = headers.map((header, index) => {
    let next = header;
    let suffix = 2;
    while (headers.slice(0, index).includes(next)) {
      next = `${header} ${suffix}`;
      suffix += 1;
    }
    return next;
  });
  const dataRows = cleanMatrix.slice(1).map((row) => uniqueHeaders.reduce((acc, header, index) => {
    acc[header] = row[index] ?? "";
    return acc;
  }, {})).filter((row) => Object.values(row).some((value) => !isBlank(value)));
  return { columns: uniqueHeaders, rows: dataRows };
}

function readFileAsText(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error("File non leggibile"));
    reader.readAsText(file, "UTF-8");
  });
}

function readFileAsArrayBuffer(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error("File non leggibile"));
    reader.readAsArrayBuffer(file);
  });
}

function loadXlsxLibrary() {
  if (window.XLSX) return Promise.resolve(window.XLSX);
  const existingScript = document.querySelector("script[data-vesta-xlsx-loader='true']");
  if (existingScript) {
    return new Promise((resolve, reject) => {
      existingScript.addEventListener("load", () => resolve(window.XLSX), { once: true });
      existingScript.addEventListener("error", () => reject(new Error("Lettore Excel non disponibile")), { once: true });
    });
  }
  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = XLSX_CDN_URL;
    script.async = true;
    script.dataset.vestaXlsxLoader = "true";
    script.onload = () => window.XLSX ? resolve(window.XLSX) : reject(new Error("Lettore Excel non disponibile"));
    script.onerror = () => reject(new Error("Lettore Excel non disponibile"));
    document.head.appendChild(script);
  });
}

async function readSpreadsheet(file) {
  const fileName = String(file?.name || "").toLowerCase();
  if (fileName.endsWith(".csv") || fileName.endsWith(".txt")) {
    const text = await readFileAsText(file);
    return rowsFromMatrix(parseCsv(text));
  }
  const XLSX = await loadXlsxLibrary();
  const buffer = await readFileAsArrayBuffer(file);
  const workbook = XLSX.read(buffer, { type: "array", cellDates: true });
  const firstSheetName = workbook.SheetNames[0];
  if (!firstSheetName) return { columns: [], rows: [] };
  const matrix = XLSX.utils.sheet_to_json(workbook.Sheets[firstSheetName], { header: 1, raw: false, defval: "" });
  return rowsFromMatrix(matrix);
}

function autoMapColumns(fields, columns) {
  const normalizedColumns = columns.map((column) => ({ column, token: normalizeToken(column) }));
  return fields.reduce((mapping, field) => {
    if (field.importable === false || field.exportOnly) return mapping;
    const tokens = [field.label, field.key, field.targetKey, ...(field.aliases || [])].map(normalizeToken).filter(Boolean);
    const exact = normalizedColumns.find((item) => tokens.includes(item.token));
    const partial = exact || normalizedColumns.find((item) => tokens.some((token) => token && (item.token.includes(token) || token.includes(item.token))));
    mapping[field.key] = partial?.column || "";
    return mapping;
  }, {});
}

function coerceValue(field, value, context) {
  if (isBlank(value)) {
    if (typeof field.defaultValue === "function") return field.defaultValue(context);
    if (Object.prototype.hasOwnProperty.call(field, "defaultValue")) return field.defaultValue;
    return undefined;
  }
  if (typeof field.parse === "function") return field.parse(value, context);
  if (field.type === "number") {
    const normalized = String(value).replace(",", ".");
    const number = Number(normalized);
    if (!Number.isFinite(number)) throw new Error(`Valore numerico non valido per ${field.label}`);
    return number;
  }
  if (field.type === "boolean") return parseBoolean(value);
  return cleanText(value);
}

function buildRecords(rows, mapping, fields) {
  const importFields = fields.filter((field) => field.importable !== false && !field.exportOnly);
  const requiredWithoutColumn = importFields.filter((field) => field.required && !mapping[field.key]);
  if (requiredWithoutColumn.length) throw new Error(`Associa i campi obbligatori: ${requiredWithoutColumn.map((field) => field.label).join(", ")}`);
  return rows.map((row, rowIndex) => {
    const record = {};
    importFields.forEach((field) => {
      const column = mapping[field.key];
      const raw = column ? row[column] : "";
      const value = coerceValue(field, raw, { row, rowIndex, field });
      if (field.required && isBlank(value)) throw new Error(`Riga ${rowIndex + 2}: campo obbligatorio mancante (${field.label})`);
      if (value !== undefined) setDeepValue(record, field.targetKey || field.key, value);
    });
    return record;
  }).filter((record) => Object.values(record).some((value) => !isBlank(value) || (typeof value === "object" && value !== null)));
}

function exportValue(row, field) {
  if (typeof field.exportValue === "function") return field.exportValue(row);
  const value = readDeepValue(row, field.targetKey || field.key);
  if (field.type === "boolean") return formatBoolean(value);
  return value ?? "";
}

function makeExportMatrix(rows, fields) {
  const exportFields = fields.filter((field) => field.exportable !== false && !field.importOnly);
  return [exportFields.map((field) => field.label), ...rows.map((row) => exportFields.map((field) => exportValue(row, field)))];
}

function downloadBlob(filename, blob) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 500);
}

function safeFileName(value) {
  return cleanText(value).toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "") || "vesta_export";
}

function escapeHtml(value) {
  return String(value ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

function exportCsv(rows, fields, fileBaseName) {
  const matrix = makeExportMatrix(rows, fields);
  const content = `\uFEFF${matrix.map((row) => row.map((value) => csvEscape(value)).join(";")).join("\r\n")}`;
  downloadBlob(`${safeFileName(fileBaseName)}.csv`, new Blob([content], { type: "text/csv;charset=utf-8" }));
}

async function exportExcel(rows, fields, fileBaseName) {
  const matrix = makeExportMatrix(rows, fields);
  try {
    const XLSX = await loadXlsxLibrary();
    const worksheet = XLSX.utils.aoa_to_sheet(matrix);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Valori");
    XLSX.writeFile(workbook, `${safeFileName(fileBaseName)}.xlsx`);
    return;
  } catch {
    const htmlRows = matrix.map((row) => `<tr>${row.map((value) => `<td>${escapeHtml(value)}</td>`).join("")}</tr>`).join("");
    const html = `<!doctype html><html><head><meta charset="utf-8"></head><body><table>${htmlRows}</table></body></html>`;
    downloadBlob(`${safeFileName(fileBaseName)}.xls`, new Blob([html], { type: "application/vnd.ms-excel;charset=utf-8" }));
  }
}

export default function ImportExportOverlay({ mode, title, entityLabel, fileBaseName, fields = [], rows = [], onClose, onImport }) {
  const [format, setFormat] = useState("excel");
  const [fileName, setFileName] = useState("");
  const [columns, setColumns] = useState([]);
  const [sourceRows, setSourceRows] = useState([]);
  const [mapping, setMapping] = useState({});
  const [working, setWorking] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState("");
  const fileInputRef = useRef(null);
  const isImport = mode === "import";
  const importFields = useMemo(() => fields.filter((field) => field.importable !== false && !field.exportOnly), [fields]);
  const exportRows = Array.isArray(rows) ? rows : [];

  useEffect(() => {
    setFormat("excel");
    setFileName("");
    setColumns([]);
    setSourceRows([]);
    setMapping({});
    setWorking(false);
    setError("");
    setResult("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, [mode, title]);

  async function handleFileChange(event) {
    const file = event.target.files?.[0];
    setError("");
    setResult("");
    setFileName(file?.name || "");
    setColumns([]);
    setSourceRows([]);
    setMapping({});
    if (!file) return;
    setWorking(true);
    try {
      const parsed = await readSpreadsheet(file);
      if (!parsed.columns.length) throw new Error("Il file non contiene intestazioni leggibili");
      setColumns(parsed.columns);
      setSourceRows(parsed.rows);
      setMapping(autoMapColumns(importFields, parsed.columns));
    } catch (err) {
      setError(err?.message || "Impossibile leggere il file selezionato");
    } finally {
      setWorking(false);
    }
  }

  function clearSelectedFile(event) {
    event.preventDefault();
    event.stopPropagation();
    if (fileInputRef.current) fileInputRef.current.value = "";
    setFileName("");
    setColumns([]);
    setSourceRows([]);
    setMapping({});
    setError("");
    setResult("");
  }

  function updateMapping(fieldKey, column) {
    setMapping((current) => ({ ...current, [fieldKey]: column }));
    setError("");
    setResult("");
  }

  async function handleExport() {
    setError("");
    setResult("");
    setWorking(true);
    try {
      if (format === "csv") exportCsv(exportRows, fields, fileBaseName || title || entityLabel);
      else await exportExcel(exportRows, fields, fileBaseName || title || entityLabel);
      setResult(`Output creato: ${exportRows.length} valori esportati`);
    } catch (err) {
      setError(err?.message || "Impossibile creare il file di output");
    } finally {
      setWorking(false);
    }
  }

  async function handleImport() {
    setError("");
    setResult("");
    setWorking(true);
    try {
      const records = buildRecords(sourceRows, mapping, importFields);
      if (!records.length) throw new Error("Nessun valore valido da importare");
      const response = await onImport(records);
      const imported = Number(response?.imported ?? records.length);
      setResult(`Import completato: ${imported} valori importati`);
    } catch (err) {
      setError(err?.message || "Impossibile importare i valori");
    } finally {
      setWorking(false);
    }
  }

  return (
    <div className="overlay_backdrop">
      <div className="overlay_card booking_overlay_card import_export_overlay_card" role="dialog" aria-modal="true" aria-label={isImport ? "Import valori" : "Output valori"}>
        <div className="overlay_header">
          <h2>{isImport ? "IMPORT VALORI" : "OUTPUT VALORI"}</h2>
          <button type="button" className="overlay_close_button" onClick={onClose} aria-label="Chiudi">×</button>
        </div>
        <div className="overlay_body import_export_overlay_body">
          <div className="overlay_section import_export_summary_section">
            <h3>{title || entityLabel || "Valori"}</h3>
            <div className={`import_export_summary_grid${isImport ? " is_import" : ""}`}>
              <div><span>Finestra</span><strong>{entityLabel || "Valori"}</strong></div>
              {isImport ? (
                <>
                  <div><span>File selezionato</span><strong>{fileName || "Nessun file"}</strong></div>
                  <div><span>Righe file</span><strong>{sourceRows.length || "-"}</strong></div>
                </>
              ) : (
                <div><span>Valori visibili</span><strong>{exportRows.length}</strong></div>
              )}
            </div>
          </div>

          {isImport ? (
            <>
              <div className="overlay_section import_export_file_section">
                <h3>File sorgente</h3>
                <label className={`import_export_file_picker${fileName ? " has_file" : ""}`}>
                  <input ref={fileInputRef} className="import_export_file_input" type="file" accept=".xlsx,.xls,.csv,.txt" onChange={handleFileChange} />
                  <span className="import_export_file_icon" aria-hidden="true">↥</span>
                  <span className="import_export_file_content">
                    <strong>{fileName || "Scegli file Excel o CSV"}</strong>
                    <small>{fileName ? `${sourceRows.length} righe lette dal file` : "Formati supportati: .xlsx, .xls, .csv, .txt"}</small>
                  </span>
                  <span className="import_export_file_button">Scegli file</span>
                </label>
                {fileName && (
                  <button type="button" className="import_export_file_clear" onClick={clearSelectedFile}>Rimuovi file selezionato</button>
                )}
              </div>

              <div className="overlay_section import_export_mapping_section">
                <h3>Associazione colonne</h3>
                <div className="import_export_mapping_head"><span>Campo collection</span><span>Colonna Excel / CSV</span></div>
                <div className="import_export_mapping_list">
                  {importFields.map((field) => (
                    <label className={`import_export_mapping_row${field.required ? " is_required" : ""}`} key={field.key}>
                      <OverlayFieldLabel required={field.required}>{field.label}</OverlayFieldLabel>
                      <VestaSelect
                        value={mapping[field.key] || ""}
                        onChange={(event) => updateMapping(field.key, event.target.value)}
                        disabled={!columns.length}
                        placeholder={field.required ? "Seleziona colonna obbligatoria" : "Lascia vuoto"}
                        options={columns.map((column) => ({ value: column, label: column }))}
                      />
                    </label>
                  ))}
                </div>
                {sourceRows.length > 0 && <p className="import_export_hint">Righe lette dal file: {sourceRows.length}. I campi non obbligatori possono restare vuoti.</p>}
              </div>
            </>
          ) : (
            <div className="overlay_section import_export_file_section">
              <h3>Formato output</h3>
              <div className="import_export_format_grid">
                <label className={`import_export_format_card${format === "excel" ? " is_active" : ""}`}>
                  <input type="radio" name="importExportFormat" value="excel" checked={format === "excel"} onChange={() => setFormat("excel")} />
                  <span>Excel</span>
                  <small>.xlsx, con fallback .xls</small>
                </label>
                <label className={`import_export_format_card${format === "csv" ? " is_active" : ""}`}>
                  <input type="radio" name="importExportFormat" value="csv" checked={format === "csv"} onChange={() => setFormat("csv")} />
                  <span>CSV</span>
                  <small>Separatore punto e virgola</small>
                </label>
              </div>
              <p className="import_export_hint">Vengono esportati solo i valori attualmente visibili sotto la lista, quindi ricerca e filtri restano rispettati.</p>
            </div>
          )}
        </div>
        <div className="overlay_footer import_export_footer">
          <div className={`overlay_error_message${result ? " is_success" : ""}`}>{error || result}</div>
          <button type="button" className="overlay_save_button" onClick={isImport ? handleImport : handleExport} disabled={working || (isImport && !sourceRows.length)}>{working ? "Elaborazione" : isImport ? "Importa Valori" : "Esporta Valori"}</button>
        </div>
      </div>
    </div>
  );
}
